import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "./api/client";

export interface AsyncState<T> {
  data: T | null;
  error: ApiError | Error | null;
  loading: boolean;
  reload: () => void;
}

/**
 * Runs an async load and tracks its outcome.
 *
 * Results are discarded if a newer load started or the component unmounted.
 * Without that, switching organizations quickly leaves whichever request
 * happened to finish last on screen — showing one organization's data under
 * another's name, which is the single worst thing this dashboard could do.
 */
export function useAsync<T>(load: (signal: AbortSignal) => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // Identifies the most recent load, so a slow earlier one cannot overwrite it.
  const latest = useRef(0);

  useEffect(() => {
    const generation = ++latest.current;
    const controller = new AbortController();

    setLoading(true);
    load(controller.signal)
      .then((result) => {
        if (generation !== latest.current) return;
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (generation !== latest.current) return;
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (generation !== latest.current) return;
        setLoading(false);
      });

    return () => controller.abort();
    // load is intentionally not a dependency: callers write it inline, and
    // depending on its identity would re-run on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, error, loading, reload };
}

/** Copies text and reports success for long enough to be noticed. */
export function useCopy(): { copied: string | null; copy: (value: string, id?: string) => void } {
  const [copied, setCopied] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = useCallback((value: string, id?: string) => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(id ?? value);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(null), 1200);
    });
  }, []);

  return { copied, copy };
}

/**
 * Closes a popover on a click outside it or on Escape.
 *
 * A menu that can only be dismissed by choosing something is a trap. The
 * listeners exist only while it is open. `container` is everything that counts
 * as inside: the popover and the control that opened it, so a second click on
 * that control toggles rather than closing and reopening.
 *
 * Escape is stopped here, because a drawer behind the popover binds Escape on
 * the document too: without it, one keystroke closed the popover and the
 * drawer, the fault the command palette once had.
 */
export function useDismiss(
  open: boolean,
  container: { current: HTMLElement | null },
  onDismiss: () => void,
): void {
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) dismiss.current();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      dismiss.current();
    };

    document.addEventListener("mousedown", onPointerDown);
    // Capture, so this runs before a drawer's own document listener.
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open, container]);
}

/**
 * A view preference, remembered in this browser for each person who signs in
 * to it.
 *
 * Per person for the reason the organization is: on a shared machine, one
 * person's choice is not the next person's. Storage can be missing or throw, in
 * a private window or with site data blocked; the preference then starts at
 * its default and lasts until the page reloads. `valid` refuses a stored value
 * this build no longer understands.
 */
export function usePreference<T extends string>(
  name: string,
  userId: string | undefined,
  fallback: T,
  valid: (value: string) => value is T,
): [T, (next: T) => void] {
  const key = userId ? `opencomms.${name}.${userId}` : null;
  const [value, setValue] = useState<T>(() => {
    if (!key) return fallback;
    try {
      const stored = window.localStorage.getItem(key);
      return stored !== null && valid(stored) ? stored : fallback;
    } catch {
      return fallback;
    }
  });

  const set = useCallback(
    (next: T) => {
      setValue(next);
      if (!key) return;
      try {
        window.localStorage.setItem(key, next);
      } catch {
        /* kept for this page only, as above */
      }
    },
    [key],
  );

  return [value, set];
}
